import React, { useState, useEffect } from 'react';
import { toChileDate } from '../../utils/chileTime';
import { evaluationsApi, enrollmentsApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  ClipboardText,
  Clock,
  CheckCircle,
  XCircle,
  Trophy,
  Warning
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

export default function WorkerEvaluationsPage() {
  const [attempts, setAttempts] = useState([]);
  const [availableEvaluations, setAvailableEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeEvaluation, setActiveEvaluation] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [attemptId, setAttemptId] = useState(null);
  const [result, setResult] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const startId = searchParams.get('start');
    if (startId) {
      handleStartEvaluation(startId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (timeRemaining > 0 && activeEvaluation) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timeRemaining, activeEvaluation]);

  const fetchData = async () => {
    try {
      const [attemptsRes, enrollmentsRes] = await Promise.all([
        evaluationsApi.getMyAttempts(),
        enrollmentsApi.getAll({ worker_id: user?.id })
      ]);
      setAttempts(attemptsRes.data);
      
      // Get evaluations from enrolled trainings where training is realizada
      const evals = [];
      for (const enrollment of enrollmentsRes.data) {
        if (enrollment.training_status === 'realizada' && enrollment.status !== 'approved') {
          try {
            const evalRes = await evaluationsApi.getByTraining(enrollment.training_id);
            evals.push(...evalRes.data.map(e => ({ ...e, training_name: enrollment.training_name })));
          } catch (e) {}
        }
      }
      setAvailableEvaluations(evals);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEvaluation = async (evaluationId) => {
    try {
      const res = await evaluationsApi.start(evaluationId);
      setActiveEvaluation(evaluationId);
      setQuestions(res.data.questions);
      setTimeRemaining(res.data.time_limit_minutes * 60);
      setAttemptId(res.data.attempt_id);
      setCurrentQuestion(0);
      setAnswers({});
      setResult(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar evaluación');
    }
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleSubmit = async () => {
    try {
      const answersArray = Object.entries(answers).map(([question_id, answer]) => ({
        question_id,
        answer
      }));

      const res = await evaluationsApi.submit({
        attempt_id: attemptId,
        answers: answersArray
      });

      setResult(res.data);
      setActiveEvaluation(null);
      fetchData();

      if (res.data.passed) {
        toast.success('¡Felicitaciones! Has aprobado la evaluación');
      } else {
        toast.error('No has alcanzado el puntaje mínimo');
      }
    } catch (error) {
      toast.error('Error al enviar evaluación');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getResultBadge = (res) => {
    if (res === 'passed') {
      return <Badge className="bg-green-500/10 text-green-400 border-green-500/20 border">Aprobado</Badge>;
    }
    if (res === 'failed') {
      return <Badge className="bg-red-500/10 text-red-400 border-red-500/20 border">Reprobado</Badge>;
    }
    return <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20 border">Pendiente</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" />
      </div>
    );
  }

  // Active Evaluation View
  if (activeEvaluation && questions.length > 0) {
    const question = questions[currentQuestion];
    const progress = ((currentQuestion + 1) / questions.length) * 100;

    return (
      <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="evaluation-active">
        {/* Timer & Progress */}
        <Card className="bg-white border-[#2B4B8C]/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="text-[#2B4B8C]/60">Pregunta {currentQuestion + 1} de {questions.length}</span>
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
                timeRemaining < 60 ? 'bg-red-500/10 text-red-400' : 'bg-[#2BBAA4]/10 text-[#2BBAA4]'
              }`}>
                <Clock size={16} />
                <span className="font-mono">{formatTime(timeRemaining)}</span>
              </div>
            </div>
            <Progress value={progress} className="h-2" />
          </CardContent>
        </Card>

        {/* Question */}
        <Card className="bg-white border-[#2B4B8C]/10">
          <CardContent className="p-6">
            <h3 className="text-xl text-[#2B4B8C] font-medium mb-6">{question.question_text}</h3>
            
            <RadioGroup
              value={answers[question.id] || ''}
              onValueChange={(value) => handleAnswer(question.id, value)}
              className="space-y-3"
            >
              {question.options.map((option, index) => (
                <div
                  key={option.id}
                  className={`flex items-center space-x-3 p-4 rounded-lg border transition-colors cursor-pointer ${
                    answers[question.id] === option.id
                      ? 'border-[#2BBAA4] bg-[#2BBAA4]/5'
                      : 'border-[#2B4B8C]/10 hover:border-white/20'
                  }`}
                  onClick={() => handleAnswer(question.id, option.id)}
                >
                  <RadioGroupItem value={option.id} id={option.id} />
                  <Label htmlFor={option.id} className="text-[#2B4B8C] cursor-pointer flex-1">
                    <span className="text-[#2B4B8C]/60 mr-2">{String.fromCharCode(65 + index)}.</span>
                    {option.text}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
            disabled={currentQuestion === 0}
            className="border-[#2B4B8C]/10"
          >
            Anterior
          </Button>
          
          {currentQuestion < questions.length - 1 ? (
            <Button
              onClick={() => setCurrentQuestion(prev => prev + 1)}
              className="bg-[#2B4B8C] hover:bg-[#2B4B8C]/80"
            >
              Siguiente
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              className="bg-[#2BBAA4] text-black hover:bg-[#239986]"
            >
              Finalizar Evaluación
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Result Dialog
  if (result) {
    return (
      <div className="max-w-md mx-auto animate-fade-in" data-testid="evaluation-result">
        <Card className="bg-white border-[#2B4B8C]/10">
          <CardContent className="p-8 text-center">
            {result.passed ? (
              <>
                <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                  <Trophy size={40} className="text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-[#2B4B8C] mb-2">¡Felicitaciones!</h2>
                <p className="text-[#2B4B8C]/60 mb-6">Has aprobado la evaluación</p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                  <XCircle size={40} className="text-red-400" />
                </div>
                <h2 className="text-2xl font-bold text-[#2B4B8C] mb-2">No aprobado</h2>
                <p className="text-[#2B4B8C]/60 mb-6">No alcanzaste el puntaje mínimo</p>
              </>
            )}

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 rounded-lg bg-[#f8fafc]">
                <p className="text-3xl font-bold text-[#2B4B8C]">{result.score.toFixed(1)}%</p>
                <p className="text-sm text-[#2B4B8C]/60">Tu puntaje</p>
              </div>
              <div className="p-4 rounded-lg bg-[#f8fafc]">
                <p className="text-3xl font-bold text-[#2B4B8C]">{result.passing_percentage}%</p>
                <p className="text-sm text-[#2B4B8C]/60">Requerido</p>
              </div>
            </div>

            <div className="flex gap-4 text-sm mb-6">
              <div className="flex items-center gap-2 text-green-400">
                <CheckCircle size={16} />
                <span>{result.correct_answers} correctas</span>
              </div>
              <div className="flex items-center gap-2 text-red-400">
                <XCircle size={16} />
                <span>{result.incorrect_answers} incorrectas</span>
              </div>
            </div>

            {result.certificate_code && (
              <div className="p-4 rounded-lg bg-[#2BBAA4]/10 border border-[#2BBAA4]/20 mb-6">
                <p className="text-sm text-[#2B4B8C]/60 mb-1">Código de Certificado</p>
                <p className="text-lg font-mono text-[#2BBAA4]">{result.certificate_code}</p>
              </div>
            )}

            <Button 
              onClick={() => setResult(null)}
              className="w-full bg-[#2BBAA4] text-black hover:bg-[#239986]"
            >
              Continuar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main View
  return (
    <div className="space-y-6 animate-fade-in" data-testid="worker-evaluations-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#2B4B8C]">Mis Evaluaciones</h1>
        <p className="text-[#2B4B8C]/60">Realiza y revisa tus evaluaciones</p>
      </div>

      {/* Available Evaluations */}
      {availableEvaluations.length > 0 && (
        <Card className="bg-white border-[#2B4B8C]/10">
          <CardHeader>
            <CardTitle className="text-[#2B4B8C] flex items-center gap-2">
              <ClipboardText size={20} className="text-[#2BBAA4]" />
              Evaluaciones Disponibles
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {availableEvaluations.map((evaluation) => (
              <div 
                key={evaluation.id}
                className="flex items-center justify-between p-4 rounded-lg bg-[#f8fafc] border border-[#2B4B8C]/5"
              >
                <div>
                  <p className="text-[#2B4B8C] font-medium">{evaluation.name}</p>
                  <p className="text-sm text-[#2B4B8C]/50">{evaluation.training_name}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-[#2B4B8C]/60">
                    <span>{evaluation.time_limit_minutes} min</span>
                    <span>{evaluation.questions_count} preguntas</span>
                    <span>{evaluation.passing_percentage}% para aprobar</span>
                  </div>
                </div>
                <Button
                  onClick={() => handleStartEvaluation(evaluation.id)}
                  className="bg-[#2BBAA4] text-black hover:bg-[#239986]"
                >
                  Iniciar
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Previous Attempts */}
      <Card className="bg-white border-[#2B4B8C]/10">
        <CardHeader>
          <CardTitle className="text-[#2B4B8C]">Historial de Evaluaciones</CardTitle>
        </CardHeader>
        <CardContent>
          {attempts.length > 0 ? (
            <div className="space-y-3">
              {attempts.map((attempt) => (
                <div 
                  key={attempt.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-[#f8fafc]"
                >
                  <div>
                    <p className="text-[#2B4B8C]">Intento #{attempt.attempt_number}</p>
                    <p className="text-sm text-[#2B4B8C]/50">
                      {attempt.started_at && format(toChileDate(attempt.started_at), "dd/MM/yyyy HH:mm", { locale: es })}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    {attempt.score !== null && (
                      <span className="text-[#2B4B8C] font-semibold">{attempt.score.toFixed(1)}%</span>
                    )}
                    {getResultBadge(attempt.result)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center py-8 text-[#2B4B8C]/50">
              No has realizado ninguna evaluación
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
